import { getProjectFallbackTarget } from '@/lib/routeGuards'
import { isPlatformUser } from '@/stores/auth'
import { useAuthStore } from '@/stores/authStore'
import { isOwnerOfOrganization } from '@/utils/permissions'
import { useNavigate, useParams } from '@tanstack/react-router'
import type React from 'react'
import { useEffect } from 'react'
import { RouteContext } from './route-context'

export type { RouteContext } from './route-context'
export { useRouteContext } from './route-context'

export function RouteContextLayout({ children }: { children: React.ReactNode }) {
	// Use strict: false to avoid invariant errors on routes without tenantId or orgId (e.g., /organizations)
	const params = useParams({ strict: false })
	const paramsRecord = params as Record<string, string>
	const tenantId = paramsRecord?.tenantId
	const orgId = paramsRecord?.orgId

	const user = useAuthStore(state => state.user)
	const navigate = useNavigate()

	useEffect(() => {
		if (!user || !orgId) {
			return
		}

		const platformUser = isPlatformUser(user) ? user : null
		if (!platformUser) {
			return
		}

		// Skip redirection for access-denied pages
		if (window.location.pathname.includes('/access-denied')) {
			return
		}

		// Check if user has access to the current view (project-level or org-level)
		let hasAccess = false
		if (tenantId) {
			hasAccess = platformUser.tenants?.includes(tenantId) || false
		} else {
			// If on org-level route, user must be the owner of the organization
			hasAccess = isOwnerOfOrganization(user, orgId)
		}

		if (hasAccess) {
			return
		}

		const fallback = getProjectFallbackTarget(platformUser, orgId, tenantId)
		if (fallback.to === '/orgs/$orgId/projects/$tenantId') {
			navigate({
				to: fallback.to,
				params: fallback.params,
				replace: true,
			})
		} else if (fallback.to === '/orgs/$orgId/inventory/$status') {
			navigate({
				to: fallback.to,
				params: fallback.params,
				replace: true,
			})
		} else if (fallback.to === '/access-denied') {
			navigate({
				to: fallback.to,
				search: fallback.search,
				replace: true,
			})
		}
	}, [user, tenantId, orgId, navigate])

	return <RouteContext.Provider value={{ tenantId, orgId }}>{children}</RouteContext.Provider>
}
